import Api from '.';
import { endpoints } from './endpoints';
import { absoluteUrl } from '../lib/homeUrl';

export default class EsignApi extends Api {

    /**
     * TKT-0003: legacy POST /esign/getEsignDetails no longer exists on the new backend.
     * Route through the new GET /esign/details and shape-bridge back into the
     * `{ data: { Data: [legacyShape] } }` envelope the saga at sagas/esign.js:19
     * (`response.data.Data[0]`) expects. Phase D will retire the legacy shape and
     * migrate the saga + downstream component to read fields directly off `data`.
     */
    async getEsignDetails(/* data unused — new endpoint takes leadId from session */) {
        const fresh = await this.detailsNew();
        const legacyShape = {
            SEGMENT: fresh && fresh.segment ? fresh.segment : '',
            ESIGNCOMPLETED: fresh && fresh.isSigned ? 'Y' : 'N',
            SHOW_NOT_ABLE_TO_ESIGN: fresh && (fresh.esignStatus === 'FAILED' || fresh.state === 'DROPPED') ? 'Y' : 'N',
            AADHARNO: null,
            ESIGNVENDOR: fresh ? fresh.esignVendor : null,
            ESIGNSTATUS: fresh ? fresh.esignStatus : null,
            ESIGNTRANSACTIONREF: fresh ? fresh.esignTransactionRef : null,
            STATE: fresh ? fresh.state : null,
            REDIRECTURL: fresh ? fresh.redirectUrl : null,
            _raw: fresh,
        };
        return { data: { Data: [legacyShape], Status: 'success' } };
    }

    getEsignDetailsKra(data) {
        let url = this.buildUrl(endpoints.esign.getEsignDetailsKra)

        return this.fetch(url, "POST", JSON.stringify(data)).then(response => response);
    }

    getEsignDetailsDdpi(data) {
        let url = this.buildUrl(endpoints.esign.getEsignDetailsDdpi)

        return this.fetch(url, "POST", JSON.stringify(data)).then(response => response);
    }

    /**
     * TKT-0003: legacy POST /esign/getAuthMode no longer exists on the new backend.
     * Vendor selection is server-driven now via the `esignVendor` field on
     * GET /esign/details; FE auth-mode UI visibility is FE-only state. Return
     * a static stub matching the legacy response envelope so downstream readers
     * (`esign.getAuthMode.OTPVisible` etc. in dadEsignFunctional) keep working.
     */
    getAuthMode(/* data unused */) {
        // Strict booleans — EsignOptions renders an EsignAuthButton iff the
        // visibility prop is truthy. JS treats '0' as truthy, so booleans
        // here prevent the Biometric / Offline / NotAble buttons from rendering.
        return Promise.resolve({
            data: {
                Status: 'success',
                Data: {
                    AUTHENTICATION_MODE: [{
                        OTPVisible: true,
                        BiometricVisible: false,
                        OFFLINEESIGN: '0',
                        NOTABLETODOESIGN: '0',
                    }],
                },
            },
        });
    }

    getAuthModeKra(data) {
        let url = this.buildUrl(endpoints.esign.getAuthModeKra)

        return this.fetch(url, "POST", JSON.stringify(data)).then(response => response);
    }

    getAuthModeDdpi(data) {
        let url = this.buildUrl(endpoints.esign.getAuthModeDdpi)

        return this.fetch(url, "POST", JSON.stringify(data)).then(response => response);
    }

    performDigioEsignRequest(data) {
        let url = this.buildUrl(endpoints.esign.performDigioEsignRequest)

        return this.fetch(url, "POST", JSON.stringify(data)).then(response => response);
    }

    performDigioEsignResponse(data) {
        let url = this.buildUrl(endpoints.esign.performDigioEsignResponse)

        return this.fetch(url, "POST", JSON.stringify(data)).then(response => response);
    }

    performDigioEsignRequestKra(data) {
        let url = this.buildUrl(endpoints.esign.performDigioEsignRequestKra)

        return this.fetch(url, "POST", JSON.stringify(data)).then(response => response);
    }

    performDigioEsignResponseKra(data) {
        let url = this.buildUrl(endpoints.esign.performDigioEsignResponseKra)

        return this.fetch(url, "POST", JSON.stringify(data)).then(response => response);
    }

    getAvailableClientCode(data) {
        let url = this.buildUrl(endpoints.esign.getAvailableClientCode)

        return this.fetch(url, "POST", JSON.stringify(data)).then(response => response);
    }

    /**
     * TKT-0003: legacy POST /esign/generateClientCode renamed to POST /esign/generate-client-code.
     * Route through the new endpoint and shape-bridge back into the legacy
     * `{ data: { Status: '1', ClientCode: '…', Message: '…', IsOffline: '0' } }`
     * envelope that the saga at `src/sagas/esign.js:217` consumes via
     * `response.data.Status` and downstream readers expect on the success branch.
     */
    async generateClientCode(data) {
        try {
            const fresh = await this.generateClientCodeNew(undefined, {
                esignType: 'FINAL',
                segment: (data && (data.Segment || data.segment)) || 'EQUITY',
            });
            const clientCode = (fresh && (fresh.clientCode || fresh.ClientCode)) || '';
            // Legacy envelope shape consumed by sagas/esign.js:215-220:
            //   saga checks `response.data.Status` (truthy) then resolves
            //   with `response.data.Data`. Component then reads
            //   `response.Status` / `response.ClientCode` / `response.IsOffline`
            //   off the resolved inner object. So Data carries the legacy fields.
            const legacyInner = {
                Status: '1',
                ClientCode: clientCode,
                Message: (fresh && fresh.message) || 'Client code generated',
                IsOffline: '0',
            };
            return {
                data: {
                    Status: '1',
                    Data: legacyInner,
                    _raw: fresh,
                },
            };
        } catch (err) {
            return {
                data: {
                    Status: '0',
                    Data: {
                        Status: '0',
                        ClientCode: '',
                        Message: (err && err.message) || 'Failed to generate client code',
                        IsOffline: '0',
                    },
                    Message: (err && err.message) || 'Failed to generate client code',
                },
            };
        }
    }

    pushDataInDrishti(data) {
        let url = this.buildUrl(endpoints.esign.pushDataInDrishti)

        return this.fetch(url, "POST", JSON.stringify(data)).then(response => response);
    }

    downloadPdfForm(data) {
        let url = this.buildUrl(endpoints.esign.downloadPdfForm)

        return this.fetch(url, "POST", JSON.stringify(data)).then(response => response);
    }

    pdfFileDownload(data) {
        let url = this.buildUrl(endpoints.esign.pdfFileDownload)

        return this.fetch(url, "POST", JSON.stringify(data)).then(response => response);
    }

    requestBiometric(data) {
        let url = this.buildUrl(endpoints.esign.requestBiometric)

        return this.fetch(url, "POST", JSON.stringify(data)).then(response => response);
    }

    emudhra(data) {
        let url = this.buildUrl(endpoints.esign.emudhra)

        return this.fetch(url, "POST", JSON.stringify(data)).then(response => response);
    }

    getEmudhra(data) {
        let url = this.buildUrl(endpoints.esign.getEmudhra)

        return this.fetch(url, "POST", JSON.stringify(data)).then(response => response);
    }

    emudhraKra(data) {
        let url = this.buildUrl(endpoints.esign.emudhraKra)

        return this.fetch(url, "POST", JSON.stringify(data)).then(response => response);
    }

    getEmudhraKra(data) {
        let url = this.buildUrl(endpoints.esign.getEmudhraKra)

        return this.fetch(url, "POST", JSON.stringify(data)).then(response => response);
    }

    emudhraDdpi(data) {
        let url = this.buildUrl(endpoints.esign.emudhraDdpi)

        return this.fetch(url, "POST", JSON.stringify(data)).then(response => response);
    }

    getEmudhraDdpi(data) {
        let url = this.buildUrl(endpoints.esign.getEmudhraDdpi)

        return this.fetch(url, "POST", JSON.stringify(data)).then(response => response);
    }

    performEmudhraEsignRequest(data) {
        let url = this.buildUrl(endpoints.esign.performEmudhraEsignRequest)

        return this.fetch(url, "POST", JSON.stringify(data)).then(response => response);
    }

    performEmudhraEsignRequestKra(data) {
        let url = this.buildUrl(endpoints.esign.performEmudhraEsignRequestKra)

        return this.fetch(url, "POST", JSON.stringify(data)).then(response => response);
    }

    performEmudhraEsignRequestDdpi(data) {
        let url = this.buildUrl(endpoints.esign.performEmudhraEsignRequestDdpi)

        return this.fetch(url, "POST", JSON.stringify(data)).then(response => response);
    }

    generateClientCodeMF(data) {
        let url = this.buildUrl(endpoints.esign.generateClientCodeMF)

        return this.fetch(url, "POST", JSON.stringify(data)).then(response => response);
    }

    performHypervergeEsignRequest(data) {
        let url = this.buildUrl(endpoints.esign.performHypervergeEsignRequest)

        return this.fetch(url, "POST", JSON.stringify(data)).then(response => response);
    }
    performHypervergeEsignResp(data) {
        let url = this.buildUrl(endpoints.esign.performHypervergeEsignResp)

        return this.fetch(url, "POST", JSON.stringify(data)).then(response => response);
    }

    // --- New eKYC 3.0 contract (Stage 13) ----------------------------------
    // Enum domains for fields on `EsignResult`. Published by BE on TKT-0061
    // BE-S13-D1/V1; mirror new values here when BE adds them.
    static ESIGN_ENUMS = {
        esignVendor: ['HYPERVERGE', 'EMUDHRA', 'NSDL', 'MANUAL', 'MOCK'],
        esignStatus: ['PENDING', 'SIGNED', 'FAILED', 'EXPIRED', 'WET_SIGN'],
        esignType: ['FINAL', 'KRA', 'DDPI'],
        postEsignVerificationResult: ['NAME_MATCH', 'NAME_MISMATCH', 'DATA_MISMATCH', 'SKIPPED'],
        stpEsignMismatchFlag: ['STP', 'NON_STP'],
        state: ['AOF_GENERATED', 'ESIGN_INITIATED', 'ESIGN_DONE', 'DROPPED'],
        segment: ['EQUITY', 'COMMODITY', 'MF', 'COMBINED'],
    };

    // The legacy methods above target the on-prem backend's many-vendor
    // surface. The new backend collapses eSign to four endpoints; vendor
    // selection (NSDL primary, eMudhra secondary) is server-driven.

    /** GET /esign/status — called when journey state = ESIGN_INITIATED. Returns { redirectUrl, ... }. */
    status() {
        return this.fetch(
            this.buildUrl(endpoints.stages.esign.status),
            'GET', undefined,
            { lead: { mode: 'query' } },
        );
    }

    /** GET /esign/details?leadId={leadId} */
    detailsNew() {
        return this.fetch(
            this.buildUrl(endpoints.stages.esign.details),
            'GET', undefined,
            { lead: { mode: 'query' } },
        );
    }

    /**
     * POST /esign/initiate
     *
     * Per TKT-0004 (closed 2026-05-14, BE commit `1b3c2bb`), `esignVendor` is now
     * an optional hint on the request — backend picks the vendor via the
     * `provider_configurations` cascade and surfaces the resolved vendor in the
     * response's `esignVendor` field. FE no longer sends it.
     *
     * Response shape (envelope.data):
     *   { esignVendor, redirectUrl, esignTransactionRef, segment, esignType, ... }
     *
     * The legacy positional `esignVendor` first arg is kept (ignored) so any
     * unmigrated caller still compiles; it goes nowhere on the wire.
     */
    initiateNew(_esignVendorIgnored, { esignType = 'FINAL', segment = 'EQUITY' } = {}) {
        // returnUrl: where the vendor should send the customer's browser back to.
        // Sent from here (rather than each of the 4 callers) so every eSign entry
        // point gets it. See absoluteUrl — the origin has to come from the running
        // page, because BE's ESign:FrontendReturnUrl hardcodes one host and this
        // app is reachable on several.
        return this.fetch(
            this.buildUrl(endpoints.stages.esign.initiate),
            'POST', JSON.stringify({ esignType, segment, returnUrl: absoluteUrl('/dad/verifyEsign') }),
            { lead: { mode: 'body' } },
        );
    }

    /**
     * POST /esign/callback
     *
     * Per TKT-0005 (followup to TKT-0004): vendor is locked in at initiate-time
     * on the BE transaction row, so the callback request no longer carries
     * `esignVendor`. Argument retained on the destructure (ignored) for callers
     * that still pass it.
     */
    callbackNew({ esignVendor: _esignVendorIgnored, esignTransactionRef, signedDocumentData }) {
        return this.fetch(
            this.buildUrl(endpoints.stages.esign.callback),
            'POST', JSON.stringify({ esignTransactionRef, signedDocumentData }),
            { lead: { mode: 'body' } },
        );
    }

    /** POST /esign/generate-client-code */
    generateClientCodeNew(esignVendor, { esignType = 'FINAL', segment = 'EQUITY' } = {}) {
        return this.fetch(
            this.buildUrl(endpoints.stages.esign.generateClientCode),
            'POST', JSON.stringify({ esignVendor, esignType, segment }),
            { lead: { mode: 'body' } },
        );
    }
}